import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";

import Fuse from "fuse.js";
import _ from "lodash";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics.ts";
import {
    SortLookup,
    useTemplates
} from "@/content/templates/template.provider";
import type {
    Author,
    Category,
    Template
} from "@/content/templates/template.types";
import {
    CategoryKey,
    CategoryTemplateOrder
} from "@/templates/software/confluence/templates/confluence-templates-page/src/config/category-template-order";
import { TEMPLATE_CATEGORY_OBJ } from "@/utils/content-queries/constants.ts";

import { Initializer } from "../../../../analytics/atl-analytics/initializer";

import { useRecommendedTemplates } from "./recommended-templates-context";

export interface TemplateSearchItem {
    id?: string | null;
    name?: string | null;
    description?: string | null;
    contributor?: string | null;
    contributorOrganization?: string | null;
    customTag?: string[] | undefined;
    type: "template" | "category";
    templateType?: string | null;
}

export interface FilterOptions {
    searchTerm?: string;
    searchItem?: TemplateSearchItem;
}

interface TemplateFuzzySearchContextProps {
    searchTerm?: string;
    setSearchTerm: (searchTerm: string) => void;
    searchSubmit: (options: FilterOptions) => void;
    setCategory: (category: Category) => void;
    matchingSearchItems: TemplateSearchItem[];
    currentCategory: Category | undefined;
    displayedTemplates: Template[];
}

const TemplateFuzzySearchContext =
    createContext<TemplateFuzzySearchContextProps | null>(null);

const useTemplateFuzzySearchContext = (): TemplateFuzzySearchContextProps => {
    const context = useContext(TemplateFuzzySearchContext);
    if (!context) {
        throw new Error(
            "useTemplateFuzzySearchContext must be used within a TemplateFuzzySearchContextProvider"
        );
    }
    return context;
};

type TemplateFuzzySearchContextProviderProps = {
    children: ReactNode;
};

export const createFuseInstance = (
    categories: Category[],
    templates: Template[]
): Fuse<TemplateSearchItem> => {
    const items: TemplateSearchItem[] = [];

    templates.forEach((template) => {
        if (template.name) {
            items.push({
                id: template.urlSlug,
                name: template.name,
                description: template.previewBlurb,
                contributor: template.contributor?.name,
                contributorOrganization: (template.contributor as Author)
                    ?.organization?.name,
                customTag: addCustomTagToTemplatesBasedOnSearchTerm(template),
                type: "template",
                templateType: template.templateType?.[0]?.templateTypeName
            });
        }
    });

    categories.forEach((category) => {
        //skip recommended and other additional categories from search
        if (
            category.urlSlug === "recommended" ||
            category.urlSlug === "project-planning" ||
            category.urlSlug === "business-strategy" ||
            category.urlSlug === "productivity" ||
            category.urlSlug === "docs-and-reports" ||
            category.urlSlug === "startup" ||
            category.urlSlug === "personal"
        ) {
            return;
        }
        if (category.name) {
            items.push({
                id: category.urlSlug,
                name: category.name,
                description: category.shortDescriptionOneLiner,
                type: "category"
            });
        }
    });

    const options = {
        keys: [
            { name: "name", weight: 0.8 }, // Increased weight for better matches on name
            { name: "description", weight: 0.5 },
            { name: "contributor", weight: 0.3 },
            { name: "contributorOrganization", weight: 0.3 },
            { name: "customTag", weight: 0.3 },
            { name: "templateType", weight: 0.2 }
        ],
        includeScore: true,
        threshold: 0.4
    };

    return new Fuse(items, options);
};

const slugToSearchTermMap: { [key: string]: string[] } = {
    "project-plan": ["proposal", "gantt", "timeline"],
    "master-project-documentation": ["proposal"],
    "annual-plan-one-pager": ["proposal"],
    "mutual-action-plan": ["proposal"],
    "executive-business-review": ["proposal"],
    "devops-runbook": ["gantt", "timeline"],
    "software-architecture-review": ["gantt"],
    "problem-framing": ["faq"],
    "safe-lean-business-case": ["faq"],
    "hr-knowledge-base": ["faq"],
    "design-sprint": ["timeline"],
    "project-kickoff": ["timeline"],
    "year-in-review": ["timeline"]
};
const WHITEBOARD_TYPE_ID = "1HlskYfRNciSnQ02SDhCPs";

// This function is used to add custom tags  for templates based on url slug
const addCustomTagToTemplatesBasedOnSearchTerm = (
    template: Template
): string[] => {
    const tags =
        (template.urlSlug ? slugToSearchTermMap[template.urlSlug] : []) ?? [];

    /**
     * Whiteboard templates for custom tag search term "whiteboard templates"
     */
    if (template.templateType?.some((type) => type.id === WHITEBOARD_TYPE_ID)) {
        tags.push("whiteboard templates");
    }

    return tags;
};

export const fuzzySearch = (
    searchTerm: string,
    fuseInstance?: Fuse<TemplateSearchItem>,
    limit = 35
): TemplateSearchItem[] => {
    if (searchTerm && fuseInstance) {
        const results = fuseInstance.search(searchTerm);
        return results
            .sort((a, b) => (a.score ?? Infinity) - (b.score ?? Infinity)) // Sort by score in ascending order
            .slice(0, limit) // Get the top results
            .map((result) => ({
                id: result.item.id,
                name: result.item.name,
                type: result.item.type,
                description: result.item.description,
                templateType: result.item.templateType
            }));
    }
    return [];
};

function getCategoryTemplates(templates: Template[], id?: string | null) {
    return templates.filter((template) => {
        return template.category?.some((category) => category.id === id);
    });
}

export const filterTemplatesByCategory = (
    templates: Template[],
    categorySlug: string | undefined | null,
    id?: string | null
) => {
    let categoryTemplates;
    // If the category is recommended, we need to check if the user is enrolled in the experiment
    // If the user is enrolled in the experiment, we need to show the experiment templates if the recommended templates from ML recommendations are empty.
    if (id === TEMPLATE_CATEGORY_OBJ.id) {
        categoryTemplates = getCategoryTemplates(templates, id);
    } else {
        // if experiment is enabled the new categories take precedence we need to show the templates that match the category slug and experiment value=true
        //if experiment is enabled but the category is not part of experiment we need to show the templates that match the category slug and experiment value=false
        //We are specifically doing this to prevent the templates from showing up from both experiment and non experiment categories having same name i.e. product-management, design & marketing-and-sales
        if (CategoryTemplateOrder[categorySlug as CategoryKey]) {
            categoryTemplates = templates.filter((template) => {
                return template.category?.some(
                    (category) => category.urlSlug === categorySlug
                );
            });
        } else {
            categoryTemplates = templates.filter((template) => {
                return template.category?.some(
                    (category) => category.urlSlug === categorySlug
                );
            });
        }
    }

    const sortLookup =
        CategoryTemplateOrder[categorySlug as CategoryKey] ?? undefined;

    return sortBy(categoryTemplates, sortLookup ?? "name");
};

function sortBy(items: Template[], sortBy?: "name" | SortLookup): Template[] {
    if (!sortBy) {
        return [...items];
    }
    if (sortBy === "name") {
        return [...items].sort((a, b) =>
            a.name && b.name ? a.name.localeCompare(b.name) : a.name ? -1 : 1
        );
    }

    return [...items].sort((a, b) => {
        const aRank = a.urlSlug ? sortBy[a.urlSlug] : undefined;
        const bRank = b.urlSlug ? sortBy[b.urlSlug] : undefined;
        // Sort by rank 1 is higher than 2
        // if aRank is undefined, it should be sorted lower than bRank
        if (aRank === undefined) {
            return bRank === undefined ? 0 : 1;
        }
        if (bRank === undefined) {
            return -1;
        }
        return aRank - bRank;
    });
}

const filterTemplatesByTemplate = (
    templates: Template[],
    templateSlug?: string | null
) => {
    return templates.filter((template) => template.urlSlug === templateSlug);
};

export const filterDisplayedTemplatesCategory = (
    templates: Template[],
    categories: Category[],
    fuseInstance: Fuse<TemplateSearchItem> | null,
    filter: FilterOptions
): {
    category: Category | undefined;
    displayedTemplates: Template[];
} => {
    if (filter.searchItem) {
        if (filter?.searchItem.type === "category") {
            const categorySlug = filter.searchItem.id;

            // Find the category that matches the slug, or use feature or recommended
            const category =
                categories.find((c) => c.urlSlug === categorySlug) ||
                categories.find((c) => c.urlSlug === "recommended");

            return {
                category,
                displayedTemplates: filterTemplatesByCategory(
                    templates,
                    category?.urlSlug ?? categorySlug,
                    category?.id
                )
            };
        } else if (filter.searchItem.type === "template") {
            return {
                category: undefined,
                displayedTemplates: filterTemplatesByTemplate(
                    templates,
                    filter.searchItem.id
                )
            };
        }
    } else if (filter.searchTerm) {
        if (!fuseInstance) {
            return { category: undefined, displayedTemplates: templates };
        }
        const searchDisplayedTemplates = fuzzySearch(
            filter.searchTerm,
            fuseInstance
        )
            // put categories at the end
            .sort((a) => (a.type === "category" ? 1 : -1))
            .map((item) => {
                if (item.type === "category") {
                    const categorySlug = item.id;
                    // Find the category that matches the slug, or use feature or recommended
                    const category = categories.find(
                        (c) =>
                            c.urlSlug === categorySlug ||
                            c.urlSlug === "recommended"
                    );
                    return filterTemplatesByCategory(
                        templates,
                        category?.urlSlug ?? categorySlug
                    );
                } else {
                    return templates.find(
                        (t) => item.type === "template" && item.id === t.urlSlug
                    );
                }
            })
            //flatten the array for templates matched to category and templates matched to search term and remove undefined values
            .flat()
            .filter((t) => t !== undefined);

        return {
            category: undefined,
            // need to remove duplicates as there could be between templates and those from the category
            displayedTemplates: _.uniqBy(searchDisplayedTemplates, "urlSlug")
        };
    }
    const category = getInitialCategory(categories);
    return {
        category: category,
        displayedTemplates: filterTemplatesByCategory(
            templates,
            category?.urlSlug,
            category?.id
        )
    };
};

const TemplateFuzzySearchContextProvider = ({
    children
}: TemplateFuzzySearchContextProviderProps) => {
    const templates = useTemplates();
    const { adjustedCategories: categories } = useRecommendedTemplates();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    // Search term used for fuzzy search
    const [searchTerm, setSearchTerm] = useState<string | undefined>(
        getInitialSearch()
    );

    // Currently selected category
    const [currentCategory, setCurrentCategory] = useState<
        Category | undefined
    >(searchTerm ? undefined : getInitialCategory(categories));

    // Current filter applied to the displayed templates
    const [currentFilter, setCurrentFilter] = useState<FilterOptions>({
        searchTerm,
        searchItem: currentCategory
            ? {
                  type: "category",
                  id: currentCategory.urlSlug,
                  name: currentCategory.name
              }
            : undefined
    });

    // Search suggestion results
    const [matchingSearchItems, setMatchingSearchItems] = useState<
        TemplateSearchItem[]
    >([]);

    // Templates to display
    const [displayedTemplates, setDisplayedTemplates] = useState<Template[]>(
        filterDisplayedTemplatesCategory(
            templates,
            categories,
            null,
            currentFilter
        ).displayedTemplates
    );

    const fuseInstance = useRef<Fuse<TemplateSearchItem>>(
        createFuseInstance(categories, templates)
    );

    const urlWithLocale = useCallback(
        (url: string) => {
            const prefix = intl.locale === "en" ? "" : `/${intl.locale}`;
            return `${prefix}${url}`;
        },
        [intl]
    );

    useEffect(() => {
        fuseInstance.current = createFuseInstance(categories, templates);
    }, [categories, templates]);

    useEffect(() => {
        if (searchTerm) {
            const fuzzySearchResults = fuzzySearch(
                searchTerm,
                fuseInstance.current
            );
            setMatchingSearchItems(fuzzySearchResults);
            ConfluenceTemplateAnalytics.sendTemplateFuzzySearchResultsAnalytic(
                fuzzySearchResults?.length.toString()
            );
        }
    }, [searchTerm, fuseInstance]);

    // Update displayed templates based on current filter
    useEffect(() => {
        if (templates.length === 0 || categories.length === 0) {
            return;
        }
        const { category, displayedTemplates: updatedDisplay } =
            filterDisplayedTemplatesCategory(
                templates,
                categories,
                fuseInstance.current,
                currentFilter || {}
            );
        setCurrentCategory(category);
        setDisplayedTemplates(updatedDisplay);
    }, [currentFilter, templates, categories]);

    // Reinitialize tracking when displayed templates changes
    useEffect(() => {
        Initializer.autoTrack();
    }, [displayedTemplates]);

    const changeSearchTerm = (newSearchTerm: string) => {
        setSearchTerm(newSearchTerm);
    };

    const searchSubmit = useCallback(
        (options: FilterOptions) => {
            setCurrentFilter(options);
            if (options.searchTerm || options.searchItem?.type === "template") {
                // Update the history and search
                history.replaceState(
                    null,
                    "",
                    urlWithLocale(
                        `/software/confluence/templates/search?term=${
                            options.searchTerm ?? options.searchItem?.name
                        }`
                    )
                );
            } else if (options.searchItem?.type === "category") {
                history.replaceState(
                    null,
                    "",
                    urlWithLocale(
                        `/software/confluence/templates/categories/${options.searchItem.id}`
                    )
                );
            }
        },
        [urlWithLocale]
    );

    const setCategory = useCallback(
        (category: Category) => {
            if (category.urlSlug === currentCategory?.urlSlug) {
                return;
            }
            setCurrentFilter({
                searchItem: {
                    type: "category",
                    id: category.urlSlug,
                    name: category.name
                }
            });

            const url = urlWithLocale(
                category.urlSlug === "recommended"
                    ? `/software/confluence/templates`
                    : `/software/confluence/templates/categories/${category.urlSlug}`
            );

            history.replaceState(null, "", url);
            createAnalyticsEvent({
                action: "viewed",
                eventType: "screen",
                name: "page",
                attributes: {
                    category: category.urlSlug,
                    page_path: url,
                    originProduct: `wac-software-confluence-templates-categories-${category.urlSlug}`,
                    isMarketingEvent: true
                }
            }).fire();
            //firing a new template category event
            ConfluenceTemplateAnalytics.viewTemplatesTaxonomyCategory(
                category.urlSlug
            );
            setSearchTerm("");
        },
        [currentCategory, urlWithLocale]
    );

    return (
        <TemplateFuzzySearchContext.Provider
            value={{
                searchTerm,
                setSearchTerm: changeSearchTerm,
                searchSubmit,
                setCategory,
                matchingSearchItems: matchingSearchItems,
                currentCategory: currentCategory,
                displayedTemplates: displayedTemplates
            }}
        >
            {children}
        </TemplateFuzzySearchContext.Provider>
    );
};

TemplateFuzzySearchContextProvider.displayName =
    "TemplateFuzzySearchContextProvider";

function getInitialSearch(): string | undefined {
    try {
        return new URLSearchParams(location.search).get("term") ?? undefined;
    } catch {
        return undefined;
    }
}

function getInitialCategory(categories: Category[]): Category | undefined {
    const term = location.pathname;
    const [categoryUrlSlug, prefix] = term.split("/").reverse();

    if (term && categories?.length > 0 && prefix === "categories") {
        const initialCategory = categories.find(
            (category) => category.urlSlug === categoryUrlSlug
        );

        if (initialCategory) {
            return initialCategory;
        }
    }
    return categories.find((category) => category.urlSlug === "recommended");
}

export {
    TemplateFuzzySearchContext as default,
    useTemplateFuzzySearchContext,
    TemplateFuzzySearchContextProvider
};
